import { useState } from 'react';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, deleteDoc, collection, query, where, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { leaveOldTeam } from '../../utils/teamUtils';
import useSnackbar from '../../hooks/useSnackbar';
import SnackbarAlert from '../SnackbarAlert/SnackbarAlert';
import { validateUsername } from '../../utils/validation';
import { logError, getUserErrorMessage, validateUserPermission } from '../../utils/security';
import {
  SettingsPaper,
  HeaderContainer,
  HeaderTitle,
  DividerStyled,
  Section,
  ChangeButton
} from './UserSettings.styles';
import CloseButton from '../CloseButton/CloseButton';

function UserSettings({ user, setUser, isModal = false, onClose }) {
  const { t } = useTranslation();
  const [newUsername, setNewUsername] = useState('');
  const [password, setPassword] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const translateValidationError = (result) => (result.errorKey ? t(result.errorKey, result.errorParams) : result.error);

  const navigate = useNavigate();
  const { openSnackbar, snackbarMessage, snackbarSeverity, showAlert, closeSnackbar } = useSnackbar();

  const handleUpdateUsername = async () => {
    try {
      // Validate user permission
      validateUserPermission(auth.currentUser?.uid, user.uid, 'username update');
      
      // Validate new username
      const usernameValidation = validateUsername(newUsername);
      if (!usernameValidation.valid) {
        showAlert(translateValidationError(usernameValidation), 'error');
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      
      // Check if username is already taken
  const q = query(collection(db, 'publicProfiles'), where('username', '==', usernameValidation.sanitized));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        showAlert(t('usernameTaken'), 'warning');
        return;
      }
      
      await Promise.all([
        updateDoc(userRef, { username: usernameValidation.sanitized }),
        setDoc(doc(db, 'publicProfiles', user.uid), { username: usernameValidation.sanitized }, { merge: true })
      ]);
      
      const updateUsernameIntheUI = async () => {
        const updatedDoc = await getDoc(userRef);
        setUser({
          ...user,
          ...updatedDoc.data()
        });
      };
      updateUsernameIntheUI();
      showAlert(t('usernameUpdated'), 'success');
      setNewUsername('');
    } catch (error) {
      logError(error, 'USERNAME_UPDATE');
      showAlert(getUserErrorMessage(error, {
        'permission-denied': t('permissionDenied'),
        genericError: t('usernameUpdateError'),
      }), 'error');
    }
  };

  const handleDeleteAccount = async () => {
    if (!showPassword) {
      setShowPassword(true);
    } else {
      setOpenDeleteDialog(true);
    }
  };

  const confirmDelete = async () => {
    setOpenDeleteDialog(false);
    if (!password.trim()) {
      showAlert(t('noPasswordError'), 'warning');
      return;
    }
    try {
      // Validate user permission
      validateUserPermission(auth.currentUser?.uid, user.uid, 'account deletion');
      
      // Step 0: Re-authenticate
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      // 1) Delete all standingTimes for the user
      const standingTimesQ = query(collection(db, 'standingTimes'), where('userId', '==', user.uid));
      const standingTimesSnap = await getDocs(standingTimesQ);
      for (const docSnap of standingTimesSnap.docs) {
        await deleteDoc(doc(db, 'standingTimes', docSnap.id));
      }

      // 2) Delete all notifications
      const notifQ = query(collection(db, 'notifications'), where('senderId', '==', user.uid));
      const notifSnap = await getDocs(notifQ);
      for (const docSnap of notifSnap.docs) {
        await deleteDoc(doc(db, 'notifications', docSnap.id));
      }
      const notifQ2 = query(collection(db, 'notifications'), where('userId', '==', user.uid));
      const notifSnap2 = await getDocs(notifQ2);
      for (const docSnap of notifSnap2.docs) {
        await deleteDoc(doc(db, 'notifications', docSnap.id));
      }

      // 3) Delete all join requests
      const jrQ = query(collection(db, 'joinRequests'), where('userId', '==', user.uid));
      const jrSnap = await getDocs(jrQ);
      for (const docSnap of jrSnap.docs) {
        await deleteDoc(doc(db, 'joinRequests', docSnap.id));
      }

      // 4) Remove user from all teams
      await leaveOldTeam(user.uid, null);

      // 5) Delete user & public profile documents
      await Promise.all([
        deleteDoc(doc(db, 'users', user.uid)),
        deleteDoc(doc(db, 'publicProfiles', user.uid))
      ]);

      // 6) Delete Firebase Auth user
      await deleteUser(auth.currentUser);

      navigate('/');
    } catch (error) {
      logError(error, 'ACCOUNT_DELETION');
      let errorMessage = t('deleteAccountError');
      if (error.code === 'auth/invalid-credential') {
        errorMessage = t('invalidPassword');
      }
      showAlert(errorMessage, 'error');
    }
  };

  return (
    <>
        <SettingsPaper isModal={isModal}>
          <HeaderContainer>
            <HeaderTitle variant="h5">
              {t('accountSettings')}
            </HeaderTitle>
            {isModal && (
              <CloseButton onClick={onClose} size="small" color="primary" sx={{ '&:hover': { transform: 'scale(1.1)' } }}>
                <CancelIcon />
              </CloseButton>
            )}
          </HeaderContainer>

          <DividerStyled />

          <Section>
            <TextField
              label={t('newUsernameLabel')}
              variant="outlined"
              fullWidth
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              size={isModal ? "small" : "medium"}
            />
            <ChangeButton
              variant="contained"
              onClick={handleUpdateUsername}
              size={isModal ? "small" : "medium"}
            >
              {t('changeUsername')}
            </ChangeButton>
          </Section>

          {showPassword && (
          <Section>
            <TextField
              label={t('password')}
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              size={isModal ? "small" : "medium"}
            />
          </Section>
          )}

          <Section>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAccount}
              size={isModal ? "small" : "medium"}
            >
              {t('deleteAccount')}
            </Button>
          </Section>
        </SettingsPaper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('deleteAccountTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteAccountMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('cancel')}</Button>
          <Button onClick={confirmDelete} autoFocus>
            {t('confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={closeSnackbar}
      />
    </>
  );
}

export default UserSettings;